import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiUserWithRolesDto } from '../models/api-user-with-roles.dto';
import { ApiRoleDto } from '../models/api-role.dto';
import { UserRoleAssignmentDto } from '../models/user-role-assignment.dto';

@Injectable({
  providedIn: 'root'
})
export class UserRolesService {
  private readonly baseUrl = `${environment.apiUrl}/user-roles`;

  constructor(private http: HttpClient) {}

  /**
   * Get all users with optional roles
   */
  getUsersWithRoles(withRoles: boolean = true, search?: string): Observable<ApiUserWithRolesDto[]> {
    let params = new HttpParams();
    if (withRoles) {
      params = params.set('withRoles', 'true');
    }
    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<ApiUserWithRolesDto[]>(this.baseUrl, { params }).pipe(
      map(users => users.sort((a, b) => (b.id ?? 0) - (a.id ?? 0)))
    );
  }

  /**
   * Get all roles
   */
  getAllRoles(): Observable<ApiRoleDto[]> {
    return this.http.get<ApiRoleDto[]>(`${environment.apiUrl}/roles`);
  }

  /**
   * Assign roles to a user
   */
  assignRolesToUser(userId: number, roleIds: number[], assignedBy: string = 'UI'): Observable<void> {
    const url = `${this.baseUrl}/${userId}/roles`;
    const payload: UserRoleAssignmentDto = { roleIds, assignedBy };
    return this.http.post<void>(url, payload);
  }

  /**
   * Update user roles (replace all roles)
   */
  updateUserRoles(userId: number, roleIds: number[], assignedBy: string = 'UI'): Observable<void> {
    const url = `${this.baseUrl}/${userId}/roles`;
    const payload: UserRoleAssignmentDto = { roleIds, assignedBy };
    return this.http.put<void>(url, payload);
  }

  /**
   * Remove all roles from a user
   */
  removeAllUserRoles(userId: number, assignedBy: string = 'UI'): Observable<void> {
    const url = `${this.baseUrl}/${userId}/roles`;
    const payload: UserRoleAssignmentDto = { roleIds: [], assignedBy };
    return this.http.put<void>(url, payload);
  }

  /**
   * Remove a specific role from a user
   */
  removeUserRole(userId: number, roleId: number): Observable<void> {
    const url = `${this.baseUrl}/${userId}/roles/${roleId}`;
    return this.http.delete<void>(url);
  }

  /**
   * Share credentials with a user via email
   */
  shareCredentials(userId: number): Observable<void> {
    const url = `${this.baseUrl}/${userId}/share-credentials`;
    return this.http.post<void>(url, {});
  }

  /**
   * Share credentials with all new users who have not received credentials yet
   */
  shareCredentialsToNewUsers(): Observable<{ processedCount: number }> {
    const url = `${this.baseUrl}/share-credentials-new-users`;
    return this.http.post<{ processedCount: number }>(url, {});
  }
}
